import { useEffect, useMemo, useState } from 'react';
import type {
  AgentApiCandidateFormsShow,
  AgentApiCandidateFormAnswersRecord,
  CandidateFormAnswerStatus,
} from '@/types/serializers';
import useFetchCandidateFormAnswers from '@/features/agent/form/api/useFetchCandidateFormAnswers';
import { cn } from '@/lib/utils';
import FormReviewDetailModal from './FormReviewDetailModal';
import FormReviewTable from './FormReviewTable';

export type StatusTab = 'unhandled' | 'registered' | 'archived';

const STATUS_TAB_CONFIG: { id: StatusTab; label: string }[] = [
  { id: 'unhandled', label: '未対応' },
  { id: 'registered', label: '登録済' },
  { id: 'archived', label: 'アーカイブ' },
];

function answerStatusToTab(status: CandidateFormAnswerStatus): StatusTab {
  switch (status) {
    case 'created':
      return 'unhandled';
    case 'synced':
      return 'registered';
    case 'failed':
      return 'archived';
    default:
      return 'unhandled';
  }
}

interface Props {
  candidateForm: AgentApiCandidateFormsShow;
  /** ホームタブの回答リンクから遷移した場合に開く回答ID（URLのanswerId） */
  initialAnswerId?: number;
  /** 詳細モーダルを閉じたときにURLからanswerIdを外すためのコールバック */
  onCloseDetailModal?: () => void;
}

export default function FormReviewTab({
  candidateForm: _candidateForm,
  initialAnswerId,
  onCloseDetailModal,
}: Props) {
  const [activeTab, setActiveTab] = useState<StatusTab>('unhandled');
  const [selectedAnswerId, setSelectedAnswerId] = useState<number | null>(null);
  const { data, isLoading, error } = useFetchCandidateFormAnswers();

  useEffect(() => {
    if (initialAnswerId != null) {
      setSelectedAnswerId(initialAnswerId);
    }
  }, [initialAnswerId]);

  const { counts, filteredRecords } = useMemo(() => {
    const records = data?.records ?? [];
    const counts: Record<StatusTab, number> = {
      unhandled: 0,
      registered: 0,
      archived: 0,
    };
    records.forEach((r: AgentApiCandidateFormAnswersRecord) => {
      const tab = answerStatusToTab(r.status);
      counts[tab] += 1;
    });
    const filteredRecords = records.filter(
      (r: AgentApiCandidateFormAnswersRecord) => answerStatusToTab(r.status) === activeTab
    );
    return { counts, filteredRecords };
  }, [data?.records, activeTab]);

  const statusTabs = useMemo(
    () => STATUS_TAB_CONFIG.map((tab) => ({ ...tab, count: counts[tab.id] })),
    [counts]
  );

  return (
    <div className="mt-xl">
      <div className="flex w-fit items-center">
        {statusTabs.map((tab, index) => {
          const isActive = activeTab === tab.id;
          const isFirst = index === 0;
          const isLast = index === statusTabs.length - 1;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={cn(
                'h-9 border border-solid border-border px-xs transition-colors',
                isFirst && 'rounded-l-sm',
                isLast && 'rounded-r-sm',
                !isFirst && '-ml-px',
                isActive
                  ? 'bg-primary_lighten text-button-md text-primary'
                  : 'bg-white text-body-sm text-sub hover:bg-gray_darken'
              )}
            >
              {tab.label} {tab.count}
            </button>
          );
        })}
      </div>
      <FormReviewTable
        statusTab={activeTab}
        answers={filteredRecords}
        isLoading={isLoading}
        error={error}
        onRowClick={(id) => setSelectedAnswerId(id)}
      />
      <FormReviewDetailModal
        answerId={selectedAnswerId}
        open={selectedAnswerId != null}
        onClose={() => {
          setSelectedAnswerId(null);
          onCloseDetailModal?.();
        }}
      />
    </div>
  );
}
